import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Building2, CalendarClock, CheckSquare, ChevronDown, Handshake, Plus, Target, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { RecordSheet, parseRecord, recordKey } from "@/components/crm/RecordSheet";
import { AppointmentDialog, CompanyDialog, ContactDialog, LeadDialog, OpportunityDialog, TaskDialog } from "@/components/crm/dialogs";
import { PipelineTab } from "@/pages/crm/PipelineTab";
import { LeadsTab } from "@/pages/crm/LeadsTab";
import { ContactsTab } from "@/pages/crm/ContactsTab";
import { CompaniesTab } from "@/pages/crm/CompaniesTab";
import { TasksTab } from "@/pages/crm/TasksTab";
import { AppointmentsTab } from "@/pages/crm/AppointmentsTab";
import { PipelineSettingsTab } from "@/pages/crm/PipelineSettingsTab";

const TABS = ["pipeline", "leads", "contacts", "companies", "tasks", "appointments", "settings"];
const RECORD_TABS = { lead: "leads", contact: "contacts", company: "companies", opportunity: "pipeline" };
const QUICK = [
  { key: "lead", icon: Target, label: "crm.newLead" },
  { key: "opportunity", icon: Handshake, label: "crm.newOpportunity" },
  { key: "contact", icon: UserRound, label: "crm.newContact" },
  { key: "company", icon: Building2, label: "crm.newCompany" },
  { key: "task", icon: CheckSquare, label: "crm.newTask" },
  { key: "appointment", icon: CalendarClock, label: "crm.newAppointment" },
];

/**
 * CRM module: one record, multiple views (Kanban / tables) + detail Sheet driven by the URL (?record=type:id)
 * so a reload or a shared link re-opens the same record. Every action goes through /api/crm/* business routes.
 */
export default function CRMPage() {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const [params, setParams] = useSearchParams();
  const record = parseRecord(params.get("record"));
  const requestedTab = params.get("tab");
  const isAdmin = hasRole("admin");
  const tab = TABS.includes(requestedTab) && (requestedTab !== "settings" || isAdmin)
    ? requestedTab : RECORD_TABS[record?.type] || "pipeline";
  const [quick, setQuick] = useState(null);
  const tabsScrollRef = useRef(null);
  const canEdit = hasRole("member");

  useEffect(() => {
    tabsScrollRef.current?.querySelector('[data-state="active"]')?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [tab]);

  const setTab = (value) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set("tab", value);
    return next;
  }, { replace: true });
  const setRecord = useCallback((next) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next) p.set("record", recordKey(next.type, next.id));
      else p.delete("record");
      return p;
    });
  }, [setParams]);
  const openRecord = useCallback((type, id) => setRecord({ type, id }), [setRecord]);

  return (
    <div className="space-y-6" data-testid="crm-page">
      <PageHeader title={t("crm.title")} subtitle={t("crm.subtitle")} testId="crm-header" actions={canEdit && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button data-testid="crm-quick-create"><Plus className="mr-2 h-4 w-4" />{t("common.create")}<ChevronDown className="ml-2 h-4 w-4 opacity-70" /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-card">
            {QUICK.map((q) => <DropdownMenuItem key={q.key} className="cursor-pointer gap-2" onSelect={() => setQuick(q.key)} data-testid={`crm-quick-create-${q.key}`}><q.icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />{t(q.label)}</DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
      )} />

      <Tabs value={tab} onValueChange={setTab}>
        <div ref={tabsScrollRef} className="og-scroll w-full min-w-0 overflow-x-auto overscroll-x-contain rounded-lg bg-muted" data-testid="crm-tabs-scroll">
          <TabsList className="h-auto w-max min-w-full justify-start gap-1 bg-muted py-1 pl-2 pr-5" data-testid="crm-tabs">
            {TABS.filter((k) => k !== "settings" || isAdmin).map((k) => <TabsTrigger key={k} value={k} className="shrink-0" data-testid={`crm-tab-${k}`}>{t(`crm.tabs.${k}`)}</TabsTrigger>)}
          </TabsList>
        </div>
        <TabsContent value="pipeline" className="mt-4"><PipelineTab onOpenRecord={openRecord} /></TabsContent>
        <TabsContent value="leads" className="mt-4"><LeadsTab onOpenRecord={openRecord} /></TabsContent>
        <TabsContent value="contacts" className="mt-4"><ContactsTab onOpenRecord={openRecord} /></TabsContent>
        <TabsContent value="companies" className="mt-4"><CompaniesTab onOpenRecord={openRecord} /></TabsContent>
        <TabsContent value="tasks" className="mt-4"><TasksTab onOpenRecord={openRecord} /></TabsContent>
        <TabsContent value="appointments" className="mt-4"><AppointmentsTab onOpenRecord={openRecord} /></TabsContent>
        {isAdmin && <TabsContent value="settings" className="mt-4"><PipelineSettingsTab /></TabsContent>}
      </Tabs>

      <RecordSheet record={record} onChange={setRecord} />

      {/* Quick create (header) */}
      <LeadDialog open={quick === "lead"} onOpenChange={(o) => !o && setQuick(null)} onSaved={(d) => openRecord("lead", d.id)} />
      <OpportunityDialog open={quick === "opportunity"} onOpenChange={(o) => !o && setQuick(null)} onSaved={(d) => openRecord("opportunity", d.id)} />
      <ContactDialog open={quick === "contact"} onOpenChange={(o) => !o && setQuick(null)} onSaved={(d) => openRecord("contact", d.id)} />
      <CompanyDialog open={quick === "company"} onOpenChange={(o) => !o && setQuick(null)} onSaved={(d) => openRecord("company", d.id)} />
      <TaskDialog open={quick === "task"} onOpenChange={(o) => !o && setQuick(null)} />
      <AppointmentDialog open={quick === "appointment"} onOpenChange={(o) => !o && setQuick(null)} />
    </div>
  );
}
